import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Password, Devices, DeviceMobile, CaretRight } from "@phosphor-icons/react";

const PWA_ENABLED = process.env.REACT_APP_PWA_ENABLED === "true";

export default function AccountSecurity() {
  const { user } = useAuth();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestReset = async () => {
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email: user?.email });
      setSent(true);
      toast.success("Password reset email sent");
    } catch (_) {
      toast.error("Could not send the reset email — try again shortly");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-2xl" data-testid="account-security">
      <p className="eyebrow">Account</p>
      <h1 className="mt-1 font-display font-extrabold tracking-tighter text-3xl sm:text-4xl leading-none">
        Account &amp; Security
      </h1>

      <section className="mt-7 border border-border bg-surface rounded-md p-5" data-testid="account-identity">
        <h2 className="t-section-title flex items-center gap-2"><ShieldCheck size={18} weight="duotone" className="text-primary" /> Identity</h2>
        <div className="mt-3 space-y-1.5">
          <p className="t-support"><span className="text-muted-foreground">Name:</span> <b data-testid="account-display-name">{user?.display_name || "—"}</b></p>
          <p className="t-support"><span className="text-muted-foreground">Email:</span> <b data-testid="account-email">{user?.email}</b></p>
        </div>
      </section>

      <section id="password" className="mt-5 border border-border bg-surface rounded-md p-5" data-testid="account-password">
        <h2 className="t-section-title flex items-center gap-2"><Password size={18} weight="duotone" className="text-primary" /> Password</h2>
        <p className="mt-2 t-support text-muted-foreground">
          We'll email you a secure link to set a new password. Changing your password signs out all other sessions.
        </p>
        <button data-testid="send-password-reset-button" disabled={busy || sent} onClick={requestReset}
          className="mt-3 border border-primary text-primary px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] rounded-md hover:bg-primary/5 disabled:opacity-50">
          {sent ? "Email sent — check your inbox" : "Send password reset email"}
        </button>
      </section>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        {PWA_ENABLED && (
        <Link to="/devices" data-testid="account-devices-link" className="border border-border bg-surface rounded-md p-5 hover:border-primary/40 transition-colors">
          <Devices size={22} weight="duotone" className="text-primary" />
          <p className="mt-2 font-display font-extrabold tracking-tight text-sm">Devices and Access</p>
          <span className="mt-1 flex items-center gap-1 text-sm text-primary font-semibold">Manage <CaretRight size={13} weight="bold" /></span>
        </Link>
        )}
        {PWA_ENABLED && (
        <Link to="/mobile-access" data-testid="account-mobile-link" className="border border-border bg-surface rounded-md p-5 hover:border-primary/40 transition-colors">
          <DeviceMobile size={22} weight="duotone" className="text-primary" />
          <p className="mt-2 font-display font-extrabold tracking-tight text-sm">Get Mobile Access</p>
          <span className="mt-1 flex items-center gap-1 text-sm text-primary font-semibold">Set up <CaretRight size={13} weight="bold" /></span>
        </Link>
        )}
      </div>
    </div>
  );
}
